/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * This file contains the component that provides context for the online patient
 * management system.
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

/**
 * Required to use React components.
 */
import * as React from 'react';

/*
 * Used to style components
 */
import { AlertTitle, Box, Button, Dialog, DialogContent, Paper, styled, Typography } from '@mui/material';

/**
 * Used for the basic page layout.
 */
import {
    InstitutionContext,
    Form,
    FormSubmitButton,
    GroupedField,
    IFormValidationError,
    IPatientSummaryUpdateListener,
    OnlinePatientManagementContext,
    PageLayout,
    PatientBreadcrumbs,
    PatientContext,
    PatientSummary,
    PatientSummaryManager,
    RouteLoading,
    Select,
    useAsyncAction,
    useFormByCodes,
    useFormLookups,
    useFormMetadata,
    useLookups,
    usePatientSave,
    useSnackbar,
    ValidationErrorType
} from '@ngt/opms';



/*
 * Used to type patient state.
 */
// import usePatientQueries from '@/hooks/data/usePatientQueries';
import PatientDetails from '@/components/common/PatientDetails';
import usePatientSummaryEventActions from '@/hooks/page/usePatientSummaryEventActions';
import { getPatientStatusSummary } from '@/utils/patientStatusUtils';
import { usePermissionsByIds } from '@ngt/opms-bctapi';
import { ResponseStatus } from '@servicestack/client';
import { IFormSubmit, IFormValidate, ValidateOn } from '@ngt/forms-core';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { LookupScreeningOutcomeType, Patient, PatientStateType, Permission, ScreeningForm, ScreeningFormPostSave } from '../api/dtos';

/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IPatientSummaryProps {
}

interface IFormDialogProps {
    open: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    form: React.ReactNode,
    loading: boolean,
    width?: "xs" | "sm" | "md" | "lg" | "xl"
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledContainer = styled(Paper)(({theme}) => ({
    padding: theme.spacing(3)
}));

const StyledTableContainer = styled(Box)(({theme}) => ({
    padding: theme.spacing(4, 0, 0, 0)
}));

const StyledDialogTitle = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3),
    fontSize: '1.25rem',
    fontWeight: 500
}))

const StyledButtonGroup = styled(Box)(({ theme }) => ({
    margin: theme.spacing(2, 0, 0, 0),
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    display: 'flex'
}))

export const FormDialog: FC<IFormDialogProps> = ({
    open,
    setOpen,
    form,
    loading,
    width
}) => {
    return (
        <Dialog open={open} onClose={() => setOpen(false)} aria-labelledby="form-dialog" maxWidth={width} fullWidth scroll="body">
            <DialogContent style={{ padding: '0' }}>
                {!loading ? form : <RouteLoading />}
            </DialogContent>
        </Dialog>
    );
}

const PatientSummaryPage: React.FunctionComponent<IPatientSummaryProps> = () => {
    const navigate = useNavigate();

    const { data: institution } = useContext(InstitutionContext);
    const { data: untypedPatient, refresh: patientRefresh } = useContext(PatientContext);

    const patient = untypedPatient as Patient;

    const patientStatusCaption = useMemo(() => {
        if (patient?.patientStateId !== PatientStateType.OnStudy) {
            return undefined;
        }

        return getPatientStatusSummary(patient.statusEvents).caption;
    }, [patient?.patientStateId, patient?.statusEvents]);

    const [markAsScreenFailDialogOpen, setMarkAsScreenFailDialogOpen] = useState<boolean>(false);
    const [screenFailSaving, setScreenFailSaving] = useState(false);
    
    const {serviceStackClient} = useContext(OnlinePatientManagementContext);
    const { enqueueSnackbar } = useSnackbar();
    const { cache } = useSWRConfig();

    const screeningFormMetadata = useFormMetadata('screening');
    const { data: screeningFormLookups } = useLookups(screeningFormMetadata);
    const formScreeningFormLookups = useFormLookups(screeningFormLookups);
    const { data: screeningForm, loading: screeningFormLoading } = useFormByCodes(
        screeningFormMetadata,
        institution?.code,
        patient?.studyNumber,
        'screening',
        1,
        1,
        false,
        false,
        false
    );

    const {
        permissions: [
            canOpmsAdmin, 
            canOpmsDataManage, 
            canViewPatient,
            canUpdatePatient
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsAdminister, // BCT staff + CRA/CSM
            Permission.OpmsDataManage,
            Permission.OpmsPatientView,
            Permission.OpmsPatientUpdate
        ], 
        undefined, 
        undefined, 
        institution?.id
    );
        
    const canAdministerOpms = useMemo(() => {
        return canOpmsAdmin || canOpmsDataManage;
    }, [canOpmsAdmin, canOpmsDataManage]);
    
    const managerRef = useRef<PatientSummaryManager>(undefined);

    useEffect(() => {
        if (!permissionsLoading) {
            if (!canViewPatient && !canAdministerOpms) {
                navigate(`/registration`, {replace: true});
            }
        }
    }, [canViewPatient, canAdministerOpms, permissionsLoading, navigate]);

    const [patientValid, setPatientValid] = useState(false);

    const { action: savePatient, pending: patientSaving } = useAsyncAction(
        usePatientSave(),
        useMemo(() => ({ 
            errorAccessor: (err) => err?.responseStatus as ResponseStatus
        }), [])
    );
    
    const updatePatientState = useCallback(async (patientStateId: PatientStateType) => {
        await savePatient(new Patient({ ...patient, previousPatientStateId: patient?.patientStateId, patientStateId: patientStateId }), false);
        await patientRefresh();
        if (managerRef.current) {
            await managerRef.current.load();
        }
        
    }, [patient, savePatient, managerRef]);

    const eventActionMapping = usePatientSummaryEventActions(
        patientSaving, 
        updatePatientState, 
        enqueueSnackbar, 
        serviceStackClient, 
        patient, 
        patientValid, 
        institution, 
        canAdministerOpms,
        managerRef.current
    )


    const onPatientValidChange: IPatientSummaryUpdateListener = useCallback((summaryState) => {
        setPatientValid(!!summaryState?.valid);
    }, [setPatientValid])

    const validateScreenFail: IFormValidate<ScreeningForm, IFormValidationError> = useCallback(async (formState) => {
        const errors: Record<string, IFormValidationError[]> = {};

        if (formState.value?.screenFailReason === undefined) {
            errors.screenFailReason = [{
                property: 'screenFailReason',
                code: 'SF-001',
                message: 'This field is required.',
                detailedMessage: 'This field is required.',
                type: ValidationErrorType.Critical
            }];
        }

        return errors;
    }, []);

    const onMarkAsScreenFailSubmit: IFormSubmit<ScreeningForm, IFormValidationError> = useCallback(async (form) => {
        setScreenFailSaving(true);

        try {
            await serviceStackClient.post(new ScreeningFormPostSave({
                form: new ScreeningForm({
                    ...form.value,
                    outcome: LookupScreeningOutcomeType.Fail
                })
            }));

            for (const key of Array.from(cache.keys()).filter(x => x.startsWith('SwrClient:ScreeningFormGetSingleByIds'))) {
                cache.delete(key);
            }

            setMarkAsScreenFailDialogOpen(false);
            await patientRefresh();
            await managerRef.current?.load();

            enqueueSnackbar(
                <>
                    <AlertTitle>Participant Marked As Screen Fail</AlertTitle>
                    Participant was marked as screen fail successfully.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            enqueueSnackbar(
                <>
                    <AlertTitle>Participant Not Marked As Screen Fail</AlertTitle>
                    {error?.responseStatus?.errorCode === 'ClientViewableException'
                        ? error.responseStatus.message
                        : 'An error occurred while attempting to mark the participant as screen fail.'}
                </>,
                { variant: 'critical' }
            );
        }
        finally {
            setScreenFailSaving(false);
        }
    }, [cache, enqueueSnackbar, patientRefresh, serviceStackClient]);
    
    return (
        <PageLayout
            breadcrumbs={<PatientBreadcrumbs />}
            heading='Participant Summary'
        >
            <StyledContainer>
                <PatientDetails patientStateCaption={patientStatusCaption} />
                <StyledTableContainer>

                    <Box
                        sx={{
                            mt: 2
                        }}
                    >
                        <PatientSummary 
                            managerRef={managerRef}
                            eventActions={eventActionMapping}
                            subscribe={{
                                onUpdate: onPatientValidChange,
                                subscription: {
                                    valid: true,
                                    data: true
                                }
                            }}
                            institutionCode={institution?.code}
                            // additionalLinks={additionalLinks}
                            enableQueries
                        />
                        {
                        <StyledButtonGroup>   
                            {
                                (patient?.patientStateId ?? 0) == PatientStateType.Consent && canUpdatePatient && (
                                    <>
                                        <Button
                                            loading={screeningFormLoading || screenFailSaving}
                                            variant="contained"
                                            color="primary"
                                            onClick={() => setMarkAsScreenFailDialogOpen(true)}
                                        >
                                            Mark As Screen Fail
                                        </Button>
                                        <Dialog
                                            open={markAsScreenFailDialogOpen}
                                            onClose={() => setMarkAsScreenFailDialogOpen(false)}
                                            aria-labelledby="screenfail-dialog-title"
                                            maxWidth="sm"
                                            fullWidth
                                        >
                                            <StyledDialogTitle
                                                id="screenfail-dialog-title"
                                                color="secondary"
                                            >
                                                Mark As Screen Fail
                                            </StyledDialogTitle>

                                            <Form
                                                initialValue={screeningForm}
                                                onSubmit={onMarkAsScreenFailSubmit}
                                                lookups={formScreeningFormLookups}
                                                validateOn={ValidateOn.onChange}
                                                onValidate={validateScreenFail}
                                            >
                                                <GroupedField
                                                    propertyPath="screenFailReason"
                                                    LabelProps={{
                                                        label: "Please provide the reason for failing the screening" as any
                                                    }}
                                                    component={Select}
                                                    FieldGridProps={{
                                                        size: {
                                                            xl: 12,
                                                            lg: 12,
                                                            md: 12,
                                                            sm: 12,
                                                            xs: 12
                                                        }
                                                    }}
                                                />

                                                <Box
                                                    display="flex"
                                                    justifyContent="flex-end"
                                                    gap={2}
                                                    padding={2}
                                                >
                                                    <Button
                                                        color="secondary"
                                                        onClick={() => setMarkAsScreenFailDialogOpen(false)}
                                                        loading={screeningFormLoading || screenFailSaving}
                                                    >
                                                        Cancel
                                                    </Button>

                                                    <FormSubmitButton
                                                        color="primary"   
                                                        variant="contained"     
                                                        loading={screeningFormLoading || screenFailSaving}
                                                    >
                                                        Mark as Screen Fail
                                                    </FormSubmitButton>
                                                </Box>
                                            </Form>
                                        </Dialog>
                                    </>
                                )
                            }
                        </StyledButtonGroup>
                        }
                    </Box>
                </StyledTableContainer>
            </StyledContainer>
        </PageLayout>
    );
}

/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default PatientSummaryPage;
